import { registerPlugin } from '@capacitor/core';

export interface BrotherDeviceInfo {
  id: string;
  name: string;
  model: string;
  connection: 'bluetooth' | 'wifi' | 'usb';
  address: string;
}

export interface BrotherDiscoverResult {
  devices: BrotherDeviceInfo[];
}

export interface BrotherConnectOptions {
  deviceId: string;
}

export interface BrotherConnectResult {
  connected: boolean;
}

export interface BrotherPrintOptions {
  deviceId: string;
  /**
   * Base64-encoded monochrome 1-bit raster bitmap, width-padded to 8-pixel rows
   * as required by the Brother QL raster command stream.
   */
  rasterBytesB64: string;
  widthPx: number;
  heightPx: number;
  copies: number;
  labelSize:
    | 'DK_22205'
    | 'DK_22210'
    | 'DK_22212'
    | 'DK_11201'
    | 'DK_11202'
    | 'DK_11204'
    | 'DK_11208';
}

export interface BrotherPrintResult {
  success: boolean;
  errorCode?: string;
}

export interface BrotherStatusOptions {
  deviceId: string;
}

export interface BrotherStatusResult {
  online: boolean;
  paperOk: boolean;
  coverClosed: boolean;
  batteryPct: number | null;
  errorCode: string | null;
}

export interface BrotherQlPlugin {
  discover(): Promise<BrotherDiscoverResult>;
  connect(options: BrotherConnectOptions): Promise<BrotherConnectResult>;
  disconnect(options: BrotherConnectOptions): Promise<void>;
  print(options: BrotherPrintOptions): Promise<BrotherPrintResult>;
  status(options: BrotherStatusOptions): Promise<BrotherStatusResult>;
}

/**
 * Capacitor plugin wrapper around the Brother Mobile SDK.
 *
 * Native implementation TODO:
 *  - iOS (Swift): link `BRLMPrinterKit.xcframework` and implement the five
 *    methods above using `BRLMPrinterDriverGenerator`.
 *  - Android (Kotlin): include `BrotherPrintLibrary.aar` and implement using
 *    `com.brother.sdk.lmprinter.PrinterDriverGenerator`.
 *  - Both native sides must convert the base64 `rasterBytesB64` into the
 *    platform image primitive and pass it to `printImage`.
 */
export const BrotherQl = registerPlugin<BrotherQlPlugin>('BrotherQl', {
  web: () => ({
    async discover() {
      return { devices: [] };
    },
    async connect() {
      return { connected: false };
    },
    async disconnect() {
      /* no-op */
    },
    async print() {
      return { success: false, errorCode: 'WEB_UNSUPPORTED' };
    },
    async status() {
      return {
        online: false,
        paperOk: false,
        coverClosed: false,
        batteryPct: null,
        errorCode: 'WEB_UNSUPPORTED',
      };
    },
  }),
});
